import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
// Direct files, not the feature's index: this route's loader ships in every
// page's first download, and the index drags in the join page, the dashboard
// and its QR code maker with it.
import { AmbassadorApiError, ambassadorsApi } from "../../../features/ambassadors/api/ambassadors.api";
import { AmbassadorProfilePage } from "../../../features/ambassadors/pages/profile-page";

/**
 * Public ambassador page: /<lang>/a/<handle>.
 *
 * Loaded on the server so the HTML carries the page (and a real 404 for a
 * handle that is unknown or not active). A renamed ambassador's old handle
 * still resolves: the API answers with the current one and we 301 there,
 * query kept, so links already posted keep working.
 */
export const Route = createFileRoute("/$lang/_layout/a/$handle")({
  validateSearch: (search: Record<string, unknown>): { cat?: string } =>
    typeof search.cat === "string" && search.cat ? { cat: search.cat } : {},
  // The chips only change `?cat=`; picking one must not refetch the page.
  staleTime: 5 * 60_000,
  loader: async ({ params, location }) => {
    let profile;
    try {
      profile = await ambassadorsApi.profile(params.handle, params.lang);
    } catch (err) {
      if (err instanceof AmbassadorApiError && (err.status === 404 || err.status === 400 || err.status === 422)) {
        throw notFound();
      }
      throw err;
    }
    if (profile.handle && profile.handle !== params.handle) {
      throw redirect({
        href: `/${params.lang}/a/${profile.handle}${location.searchStr ?? ""}`,
        statusCode: 301,
      });
    }
    return { profile };
  },
  component: ProfileRoute,
});

function ProfileRoute() {
  const { profile } = Route.useLoaderData();
  const { lang } = Route.useParams();
  const { cat } = Route.useSearch();
  return <AmbassadorProfilePage profile={profile} lang={lang} cat={cat ?? null} />;
}
